const validator = require("./validator");
define("tmds/SvgElement", [
	"jquery",
	"d3",
	"tmds/geometry/Geometry",
	"tmds/symbols/Symbol",
	"tmds/utils/geometryUtils",
	"tmds/utils/svgUtils",
	"tmds/utils/symbolUtils",
], function ($, d3, Geometry, Symbol, geomUtils, svgUtils, symUtils) {
	"use strict";

	/**
	 * construct a new svgElement
	 * @param options {Object} option object with below structure
	 * { 
	 * 		geometry: {
	 * 			x: 10,
	 * 			y: 69
	 * 		},
	 * 		symbol: {
	 * 			color: [255,0,0,128],
	 * 			size: 12,
	 * 			xOffset: 0,
	 * 			yOffset: 0,
	 * 			type:"SimplePointSymbol",
	 * 			style:"square"
	 * 		},
	 * 		attributes: {
	 * 			XCoord: 10,
	 * 			YCoord: 69,
	 * 			Plant: "Mesa Mint"
	 * 		}
	 * }
	 * @note all option members are optional
	 */
	var SvgElement = function (options) {
		this.geometry = null;
		this.symbol = null;
		this.visible = true;
		this.attributes = null;
		this._svgNode = null;
		this._layer = null;

		if (options != undefined) {
			if (validator.isDataObject(options)) {
				this.geometry = options.geometry ? geomUtils.fromJson(options.geometry) : null;
				this.symbol = options.symbol ? symUtils.fromJson(options.symbol) : (this.geometry ? symUtils.fromSpatialType(this.geometry.type) : null);
				setAttributes.call(this, options.attributes);
			}
			else {
				throw new Error('Invalid options values - ' + options + ' passed');
			}
		}
	};

	/**
	 * show the svgElement 
	 */
	function show() {
		this.visible = true;
		d3.select(this._svgNode).classed("hide", false);
	}

	/**
	 * hide the svgElement
	 */
	function hide() {
		this.visible = false;
		d3.select(this._svgNode).classed("hide", true);
	}

	/**
	 * get the element hosting layer
	 * @return {tmds.layers.Layer}
	 */
	function getLayer() {
		return this._layer;
	}

	/**
	 * set the hosting layer of the element
	 */
	function setLayer(layer) {
		if (layer && layer.declaredClass === "tmds.layers.Layer") {
			this._layer = layer;
			return this;
		}
		else {
			throw new Error('Invalid layer value - ' + layer + ' passed');
		}
	}

	/**
	 * get the svg node of the element
	 * @return {SVGDOMElement}
	 */
	function getNode() {
		return this._svgNode;
	}

	/**
	 * set the svg node of the element
	 * @param containerNode {SVGElemet} parent svd node that hosts this svgElement
	 */
	function createNode() {
		var layerNode = this._layer && this._layer.getNode();

		// if _layerNode not available, skip
		if (!layerNode) {
			return null;
		}

		// create svgNode based on the geometry and symbol
		var elemContainer = d3.select(layerNode).append("g");
		var elemSvgProps = svgUtils.getSvgAttrs(this);

		elemSvgProps.forEach(function (svgProp) {
			var svgChildElem = elemContainer.append(svgProp.type);

			for (var key in svgProp.attrs) {
				if (svgProp.attrs.hasOwnProperty(key)) {
					svgChildElem.attr(key, svgProp.attrs[key]);
				}
			}

			if (svgProp.type === "text") {
				svgChildElem.text(svgProp.text);
			}
		});

		this._svgNode = elemContainer.node();
	}

	/**
	 * set the element geometry
	 * @param geom {tmds.geometry.Geometry}
	 * @return the svgElement
	 */
	function setGeometry(geom) {
		if (geom instanceof Geometry) {
			this.geometry = geom;
			redraw.call(this);

			return this;
		}
		else {
			throw new Error('Invalid geometry value - ' + geom + ' passed');
		}

	}

	/**
	 * set the element symbol
	 * @param symbol {tmds.symbols.Symbol}
	 * @return the svgElement
	 */
	function setSymbol(symbol) {
		if (symbol instanceof Symbol) {
			this.symbol = symbol;
			redraw.call(this);

			return this;
		}
		else {
			throw new Error('Invalid symbol value - ' + symbol + ' passed');
		}
	}

	/**
	 * set attributes
	 * @param attrs {Object} data object for attributes
	 * @return the svgElement
	 */
	function setAttributes(attrs) {
		if (attrs) {
			if (validator.isDataObject(attrs)) {
				this.attributes = attrs;
				return this;
			}
			else {
				throw new Error('Invalid attributes value - ' + attrs + ' passed');
			}
		}

		return this;
	}

	/**
	 * redraw the svgElement
	 * when geometry or symbol be updated, need to call this method to redraw the svgNode.
	 */
	function redraw() {
		if (!this._svgNode) {
			return;
		}

		var childNodes = this._svgNode.childNodes;
		var elemSvgProps = svgUtils.getSvgAttrs(this);

		$.each(elemSvgProps, function (idx, svgProp) {
			var svgElem = d3.select(childNodes[idx]);

			for (var key in svgProp.attrs) {
				if (svgProp.attrs.hasOwnProperty(key)) {
					svgElem.attr(key, svgProp.attrs[key]);
				}
			}

			if (svgProp.type === "text") {
				svgElem.text(svgProp.text);
			}
		});
	}

	/**
	 * convert SvgElement object to JSON representation
	 */
	function toJson() {
		return {
			geometry: this.geometry ? this.geometry.toJson() : null,
			symbol: this.symbol ? this.symbol.toJson() : null,
			attributes: this.attributes ? $.extend({}, this.attributes) : null
		};
	}

	SvgElement.prototype = {
		declaredClass: "tmds.SvgElement",
		'show': show,
		'hide': hide,
		'getLayer': getLayer,
		'setLayer': setLayer,
		'getNode': getNode,
		'createNode': createNode,
		'setGeometry': setGeometry,
		'setSymbol': setSymbol,
		'setAttributes': setAttributes,
		'redraw': redraw,
		'toJson': toJson
	};

	return SvgElement;
});